import React, { useState, useEffect, useRef } from 'react';
import { Card, Spinner, Alert, Form, Modal, Button } from 'react-bootstrap';
import api from '../../services/api';

const ProcessGraph = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [graphImage, setGraphImage] = useState(null);
  const [dbSource, setDbSource] = useState('mixed_events');
  const [graphType, setGraphType] = useState('ddfg1');
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  
  const imageContainerRef = useRef(null);

  const fetchProcessGraph = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getProcessGraph(dbSource, graphType);
      if (response.data.error) {
        setError(response.data.error);
      } else {
        setGraphImage(response.data.image);
      }
    } catch (err) {
      console.error('Error fetching process graph:', err);
      setError('Failed to load process graph. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcessGraph();
  }, [dbSource, graphType]);
  
  // Reset zoom and position when the modal opens
  useEffect(() => {
    if (showZoomModal) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [showZoomModal]);
  
  // Handle zoom functionality
  const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 3)); // Limit max zoom to 3x
  };
  
  const handleZoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5)); // Limit min zoom to 0.5x
  };
  
  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };
  
  // Handle dragging for panning the image
  const handleMouseDown = (e) => {
    // Prevent default browser image drag behavior
    e.preventDefault();
    
    setIsDragging(true);
    setStartPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };
  
  const handleMouseMove = (e) => {
    if (isDragging) {
      e.preventDefault();
      setPosition({
        x: e.clientX - startPosition.x,
        y: e.clientY - startPosition.y
      });
    }
  };
  
  const handleMouseUp = (e) => {
    if (isDragging) {
      e.preventDefault();
      setIsDragging(false);
    }
  };
  
  // Also handle mouse leave to prevent sticky dragging
  const handleMouseLeave = (e) => {
    if (isDragging) {
      setIsDragging(false);
    }
  };
  
  // Set up global mouse up handler when modal is open
  useEffect(() => {
    if (showZoomModal) {
      // Add global mouseup handler to ensure drag stops even if outside the component
      const handleGlobalMouseUp = () => {
        if (isDragging) {
          setIsDragging(false);
        }
      };
      
      window.addEventListener('mouseup', handleGlobalMouseUp);
      
      // Clean up event listener when modal closes or component unmounts
      return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [showZoomModal, isDragging]);

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Process Graph</h5>
          <div className="d-flex">
            <Form.Select 
              size="sm" 
              className="me-2" 
              style={{ width: '140px' }}
              value={dbSource}
              onChange={(e) => setDbSource(e.target.value)}
            >
              <option value="mixed_events">Mixed Events</option>
              <option value="events">Events</option>
              <option value="source_events">Source Events</option>
              <option value="message_events">Message Events</option>
            </Form.Select>
            <Form.Select 
              size="sm" 
              style={{ width: '100px' }}
              value={graphType}
              onChange={(e) => setGraphType(e.target.value)}
            >
              <option value="ddfg1">Type 1</option>
              <option value="ddfg2">Type 2</option>
            </Form.Select>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="text-center">
        {loading ? (
          <div className="py-5">
            <Spinner animation="border" role="status" variant="primary" />
            <p className="mt-2">Loading process graph...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : graphImage ? (
          <img 
            src={`data:image/png;base64,${graphImage}`} 
            alt="Process Graph" 
            className="img-fluid cursor-pointer" 
            style={{ maxHeight: '500px', cursor: 'pointer' }}
            onClick={() => setShowZoomModal(true)}
            title="Click to zoom"
          />
        ) : (
          <Alert variant="info">No process graph data available.</Alert>
        )}
      </Card.Body>
      
      {/* Zoom Modal */}
      <Modal 
        show={showZoomModal} 
        onHide={() => setShowZoomModal(false)} 
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Process Graph Viewer</Modal.Title>
        </Modal.Header>
        <Modal.Body 
          className="text-center position-relative overflow-hidden" 
          style={{ height: '70vh' }}
          ref={imageContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <div 
            className="d-inline-block" 
            style={{ 
              transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
              transformOrigin: '0 0',
              transition: isDragging ? 'none' : 'transform 0.1s ease-out'
            }}
          >
            <img 
              src={`data:image/png;base64,${graphImage}`} 
              alt="Process Graph" 
              style={{ 
                maxWidth: '100%',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
                WebkitUserDrag: 'none', // Prevent image drag in WebKit browsers
                pointerEvents: 'none' // Make the image itself not receive pointer events
              }} 
              draggable="false"
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div>
            <span className="me-2">Zoom: {Math.round(scale * 100)}%</span>
            <Button variant="outline-secondary" size="sm" onClick={handleZoomOut} className="me-1">
              <i className="bi bi-zoom-out"></i> Zoom Out
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={handleZoomIn} className="me-1">
              <i className="bi bi-zoom-in"></i> Zoom In
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={handleResetZoom}>
              <i className="bi bi-arrow-counterclockwise"></i> Reset
            </Button>
          </div>
          <Button variant="secondary" onClick={() => setShowZoomModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ProcessGraph;
